import { LoadingOverlay, Modal, Space } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useParams } from 'react-router-dom';
import { SmallModal } from '../../../components/SmallModal';
import { Title } from '../../../components/Title';
import { FormButtons } from '../../../components/form/FormButtons';
import { mutate } from '../../../util/api/mutate';
import { useUpsertPrescriptionForm } from '../hooks/useUpsertPrescriptionForm';
import type { UpsertPrescriptionDto } from '../types/PrescriptionDto';
import { UpsertPrescriptionForm } from './UpsertPrescriptionForm';

interface NewPrescriptionModalProps {
	close: () => void;
	opened: boolean;
	refreshTable: () => void;
}

export function NewPrescriptionModal({
	close,
	opened,
	refreshTable,
}: NewPrescriptionModalProps) {
	const { organisation } = useParams();

	const [
		showSuccessModal,
		{ open: openSuccessModal, close: closeSuccessModal },
	] = useDisclosure(false);

	const { form, inputs } = useUpsertPrescriptionForm();

	const newPrescriptionMutation = mutate<UpsertPrescriptionDto, number>(
		`${organisation}/prescriptions/new`,
		inputs,
		'multipart/form-data',
	);

	const cancelClose = () => {
		form.reset();
		close();
		closeSuccessModal();
	};

	const successClose = () => {
		cancelClose();
		refreshTable();
	};

	return (
		<Modal
			opened={opened}
			onClose={cancelClose}
			title={<Title label={'New Prescription'} />}
			withCloseButton={true}
			size={'xl'}
			centered
		>
			<LoadingOverlay visible={newPrescriptionMutation.isLoading} />
			<SmallModal
				isOpen={showSuccessModal}
				close={successClose}
				title={'Prescription Added!'}
				body={
					'The prescription record has been successfully added. Click Ok to continue...'
				}
			/>
			<Space h={6} />
			<form
				onSubmit={form.onSubmit(async () => {
					await newPrescriptionMutation.mutateAsync().then((response) => {
						if (response.isValid) {
							openSuccessModal();
						}
					});
				})}
			>
				<UpsertPrescriptionForm form={form} />
				<Space h={20} />
				<FormButtons close={cancelClose} />
			</form>
		</Modal>
	);
}
